/*
 * authClient: el token de acceso vive aquí, en memoria, y se renueva con la
 * cookie de refresh de /v3/auth. Es el único módulo que habla con /v3/auth y
 * nunca escribe el token en localStorage.
 *
 * Copia adaptada de levita-front/src/app/auth/authClient.js (spec fase 3 §4:
 * mismo diseño y mismos tests en los dos repos, copiado, no compartido). Las
 * diferencias son cuatro constantes: CLAVE_VIEJA, PLATAFORMA, la base de la URL
 * y el origen del dominio, más tres divergencias de código, cada una anotada
 * en su sitio y en la sección de auth del CLAUDE.md raíz:
 *   - LEVITA_URL se importa de constants/ApiConstant.js en vez de recalcularse;
 *   - el evento `session` lleva `{deAfuera}` (mini enrola sin pasar por thunk);
 *   - el usuario de la sesión se conserva en memoria y sale por estado().
 *
 * getAccessToken() es síncrono a propósito: es el reemplazo textual de
 * localStorage.getItem('auth_token') y de state.auth.token en el codemod.
 * Quien necesita renovar (axios, urql, fetch) usa refresh().
 */

// DIVERGENCIA con levita-front, que arma la URL a partir de su propia
// configuración: aquí LEVITA_URL vive en constants/ApiConstant.js, una hoja sin
// imports, para no duplicar el valor ni cerrar el ciclo con utils/axios-request
// (que importa este módulo).
import { LEVITA_URL } from 'constants/ApiConstant';

export { LEVITA_URL };

// eslint-disable-next-line no-restricted-syntax
export const CLAVE_VIEJA = 'auth_token';
export const PLATAFORMA = 'mini';
export const CANAL = 'levita_auth';
export const CANDADO = 'levita_refresh';
export const ANTICIPO_MS = 120 * 1000;
export const UMBRAL_VISIBLE_MS = 60 * 1000;
export const ESPERA_REINTENTO_MS = 5000;
export const ESPERA_MAXIMA_MS = 60000;
export const MAX_REINTENTOS = 6;
// Un refresco cuya respuesta llega después de un logout/revocación se descarta
// con este código: no es un fallo del server y no dispara eventos.
export const CODIGO_CANCELADO = 'CANCELLED';
export const CODIGOS_DE_SESION = [
  'SESSION_REVOKED_NEW_DEVICE',
  'SESSION_EXPIRED',
  'SESSION_REVOKED',
  'BLACKLISTED',
];

export class AuthError extends Error {
  constructor({ code, message, status, retryAfterMinutes }) {
    super(message || code);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
    // super() cae al code cuando no hay texto; aquí queda solo lo que mandó el
    // server, que es lo que la UI puede mostrar tal cual (BLACKLISTED, §3.4).
    this.serverMessage = message || '';
    this.retryAfterMinutes = retryAfterMinutes;
  }
}

const porDefecto = {
  fetchImpl: (...args) => window.fetch(...args),
  base: () => `${LEVITA_URL}/v3/auth`,
  storage: () => window.localStorage,
  ahora: () => Date.now(),
  crearCanal: () =>
    typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel(CANAL),
  locks: () =>
    typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks
      : null,
  documento: () => (typeof document === 'undefined' ? null : document),
  setTimeoutImpl: (...args) => setTimeout(...args),
  clearTimeoutImpl: (...args) => clearTimeout(...args),
  aleatorio: () => Math.random(),
};

export function crearAuthClient(deps = {}) {
  const d = { ...porDefecto, ...deps };

  let accessToken = '';
  let expiresAt = 0;
  let session = null;
  // DIVERGENCIA con levita-front: el usuario que viene con la sesión
  // (/login, /refresh, /mfa/totp, /mfa/totp/activate) se conserva aquí. Lo lee
  // authSlice.adoptarSesion para no tener que pedir /session en boot y en cada
  // login, y para que un /session caído (503, red) no deje la UI de invitado
  // con una sesión viva detrás.
  let usuario = null;
  // Estado del refresco (Task 2): la promesa en vuelo, el temporizador de
  // renovación anticipada y el contador de reintentos de SESSION_UNAVAILABLE.
  let refrescando = null;
  let temporizador = null;
  let reintentos = 0;
  let temporizadorReintento = null;
  // Enfriamiento tras un fallo transitorio (server caído, red): cada 401
  // TOKEN_EXPIRED de cada petición en vuelo pediría su propio refresco forzado
  // y el cliente martillearía al server que ya está mal. Se recuerda cuándo
  // falló y con qué, y durante ESPERA_REINTENTO_MS se contesta con ese mismo
  // error sin salir a la red. El reintento agendado sí pasa.
  let ultimoFalloTransitorio = 0;
  let ultimoErrorTransitorio = null;
  // Época del estado de sesión: limpiar() la incrementa, así que un refresco
  // que ya estaba en vuelo sabe que su respuesta llegó tarde.
  let generacion = 0;
  const listeners = {};
  const canal = d.crearCanal();
  const doc = d.documento();

  function emitir(evento, ...detalle) {
    (listeners[evento] || []).slice().forEach((fn) => {
      fn(...detalle);
    });
  }

  function on(evento, fn) {
    listeners[evento] = [...(listeners[evento] || []), fn];
    return () => {
      listeners[evento] = (listeners[evento] || []).filter((f) => {
        return f !== fn;
      });
    };
  }

  async function pedirUnaVez(
    path,
    { method = 'POST', body, conToken = false } = {}
  ) {
    const headers = { 'Content-Type': 'application/json' };
    if (conToken && accessToken) headers.Authorization = accessToken;
    let resp;
    try {
      resp = await d.fetchImpl(`${d.base()}${path}`, {
        method,
        headers,
        credentials: 'include',
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      // Un transporte roto (o un fetch que no devuelve Response) es red caída:
      // pedir() nunca debe dejar escapar un error que no sea AuthError.
      if (!resp || typeof resp.json !== 'function')
        throw new Error('respuesta inválida');
    } catch (e) {
      throw new AuthError({ code: 'NETWORK', message: e.message, status: 0 });
    }
    let data = null;
    try {
      data = await resp.json();
    } catch (e) {
      data = null;
    }
    if (!resp.ok) {
      if (!data || !data.code) {
        throw new AuthError({
          code: 'NETWORK',
          message: `HTTP ${resp.status}`,
          status: resp.status,
        });
      }
      throw new AuthError({
        code: data.code,
        message: data.message,
        status: resp.status,
        retryAfterMinutes: data.retryAfterMinutes,
      });
    }
    return data || {};
  }

  // Las llamadas con sesión (conToken) van por fetch pelón: si el access token
  // ya venció —una pestaña que durmió, un envío justo dentro de la ventana de
  // renovación— el server contesta 401 TOKEN_EXPIRED aunque la cookie de
  // refresh siga buena, y sin esto el usuario vería "Tu sesión expiró" por
  // nada. Se refresca una vez, forzando (el reloj local puede ir adelantado) y
  // se repite la petición. Si el refresco tampoco sirve sale el error original.
  // /logout va con `sinReintento`: cerrar sesión tiene que ser idempotente y no
  // debe resucitar un token en el camino. /refresh, /login y /mfa/totp no son
  // conToken, así que ni entran aquí (no hay recursión posible).
  async function pedir(path, opciones = {}) {
    const {
      conToken = false,
      sinReintento = false,
      silencioso = false,
    } = opciones;
    if (!conToken) return pedirUnaVez(path, opciones);
    let error;
    try {
      return await pedirUnaVez(path, opciones);
    } catch (e) {
      error = e;
    }
    if (
      !silencioso &&
      !sinReintento &&
      error.status === 401 &&
      error.code === 'TOKEN_EXPIRED'
    ) {
      let refrescado = true;
      try {
        await refresh({ forzar: true });
      } catch (e) {
        refrescado = false;
      }
      if (refrescado) {
        try {
          return await pedirUnaVez(path, opciones);
        } catch (e) {
          error = e;
        }
      }
    }
    // Mismo trato que le dan las capas HTTP a un 401/403/503: si el código dice
    // que la sesión ya no existe, esta pestaña se entera aquí (recibirCodigo
    // filtra los que no son de sesión).
    // `silencioso` es para las lecturas de fondo (el aviso de MFA): que una
    // sesión ya muerta no dispare la revocación desde una llamada que el
    // usuario ni pidió; quien sí importa se enterará por su propia petición.
    if (
      !silencioso &&
      (error.status === 401 || error.status === 403 || error.status === 503)
    ) {
      recibirCodigo(error.code, error.serverMessage);
    }
    throw error;
  }

  function cancelarTemporizador() {
    if (temporizador !== null) {
      d.clearTimeoutImpl(temporizador);
      temporizador = null;
    }
  }

  function cancelarReintento() {
    if (temporizadorReintento !== null) {
      d.clearTimeoutImpl(temporizadorReintento);
      temporizadorReintento = null;
    }
  }

  // Renovación anticipada: 120 s antes de que venza el token. El piso de 1 s
  // cubre un token ya casi vencido.
  function programar() {
    cancelarTemporizador();
    const enMs = Math.max(expiresAt - d.ahora() - ANTICIPO_MS, 1000);
    temporizador = d.setTimeoutImpl(() => {
      return refresh().catch(() => {});
    }, enMs);
  }

  function difundir(mensaje) {
    if (canal) canal.postMessage(mensaje);
  }

  // adoptar guarda una sesión emitida por el server (o recibida de otra
  // pestaña). 'session' solo se emite al pasar de sin token a con token:
  // un refresco no es un login nuevo. Lo que llega de otra pestaña no se
  // re-difunde (si no, dos pestañas se rebotarían el token para siempre).
  // Devuelve true solo si adoptó. Un 2xx sin accessToken, o con un
  // vencimiento que no sale un número (sin expiresAt ni expiresIn), no es una
  // sesión: adoptarlo dejaría el temporizador y la guarda de frescura rotos.
  function adoptar(emitida, { deAfuera = false } = {}) {
    if (
      !emitida ||
      typeof emitida.accessToken !== 'string' ||
      !emitida.accessToken
    )
      return false;
    const vence = emitida.expiresAt || d.ahora() + emitida.expiresIn * 1000;
    if (!Number.isFinite(vence)) return false;
    const habiaToken = accessToken !== '';
    accessToken = emitida.accessToken;
    expiresAt = vence;
    session = emitida.session || null;
    // Un /refresh que no trae usuario no borra el que ya teníamos.
    if (emitida.user) usuario = emitida.user;
    reintentos = 0;
    ultimoFalloTransitorio = 0;
    ultimoErrorTransitorio = null;
    programar();
    if (!deAfuera) difundir({ type: 'token', accessToken, expiresAt, session });
    // DIVERGENCIA con levita-front (que emite solo `emitir('session', session)`):
    // el segundo argumento le dice al oyente si la sesión llegó de otra pestaña.
    // En mini, /auth/enrolar llama a authClient.activate() directo, sin thunk,
    // así que el oyente de sesionRedux ve !token && !booting && !loading y
    // recargaría la página a mitad del enrolamiento. Con `deAfuera` distingue
    // "otra pestaña entró" (recargar) de "esta pestaña acaba de conseguir
    // sesión" (adoptarla). Mismo estilo que `revoked`, que va con (code, message).
    if (!habiaToken) emitir('session', session, { deAfuera });
    return true;
  }

  // Cerrar la sesión invalida todo lo que estuviera en vuelo o agendado: sube
  // la época y apaga los dos temporizadores.
  function limpiar() {
    accessToken = '';
    expiresAt = 0;
    session = null;
    usuario = null;
    generacion += 1;
    ultimoFalloTransitorio = 0;
    ultimoErrorTransitorio = null;
    cancelarTemporizador();
    cancelarReintento();
  }

  if (canal) {
    canal.onmessage = ({ data }) => {
      if (!data) return;
      if (data.type === 'token' && data.expiresAt > expiresAt) {
        adoptar(data, { deAfuera: true });
      } else if (data.type === 'logout') {
        limpiar();
        emitir('guest');
      } else if (data.type === 'revoked') {
        limpiar();
        emitir('revoked', data.code, data.message);
      }
    };
  }

  // El candado es entre pestañas; el single-flight de refresh() es dentro de
  // la pestaña. Sin navigator.locks se ejecuta igual, solo sin exclusión.
  function conCandado(fn) {
    const locks = d.locks();
    if (locks && typeof locks.request === 'function') {
      return locks.request(CANDADO, () => {
        return fn(true);
      });
    }
    return fn(false);
  }

  function cancelado() {
    return new AuthError({
      code: CODIGO_CANCELADO,
      message: 'refresco cancelado',
      status: 0,
    });
  }

  async function refrescarDeVerdad(trasCandado, forzar) {
    const tokenAlIniciar = accessToken;
    let intento = 0;
    for (;;) {
      // Solo si esperamos el candado: otra pestaña pudo rotar la cookie y
      // difundir un token fresco mientras tanto, así que no hay nada que
      // pedir. Se re-evalúa en cada vuelta, no solo en la primera.
      if (
        !forzar &&
        trasCandado &&
        accessToken &&
        expiresAt - d.ahora() > ANTICIPO_MS
      )
        return accessToken;
      const gen = generacion;
      let emitida;
      try {
        // eslint-disable-next-line no-await-in-loop
        emitida = await pedir('/refresh');
      } catch (e) {
        // La sesión se cerró mientras pedíamos: la respuesta ya no es nuestra.
        if (generacion !== gen) throw cancelado();
        // La gracia de 60 s del server: nuestra cookie pudo quedar vieja por
        // un refresh de otra pestaña. Una vez más con la cookie nueva.
        if (e.code === 'REFRESH_INVALID' && intento === 0) {
          intento += 1;
          continue;
        }
        // Otra pestaña nos difundió un token bueno mientras pedíamos: que la
        // nuestra fallara no es motivo para cerrar nada.
        if (accessToken && accessToken !== tokenAlIniciar) return accessToken;
        throw e;
      }
      if (generacion !== gen) throw cancelado();
      if (!adoptar(emitida)) {
        throw new AuthError({
          code: 'NETWORK',
          message: 'respuesta sin token',
          status: 200,
        });
      }
      return accessToken;
    }
  }

  // Espera exponencial con jitter: 5 s, 10 s, 20 s... con techo de 60 s y un
  // ±20 % para que N pestañas (o N clientes) no vuelvan todas en el mismo
  // instante sobre un server que apenas se está levantando.
  function esperaDeReintento() {
    const base = Math.min(
      ESPERA_REINTENTO_MS * 2 ** (reintentos - 1),
      ESPERA_MAXIMA_MS
    );
    return Math.round(base * (0.8 + d.aleatorio() * 0.4));
  }

  function programarReintento() {
    if (reintentos >= MAX_REINTENTOS || temporizadorReintento !== null) return;
    reintentos += 1;
    temporizadorReintento = d.setTimeoutImpl(() => {
      temporizadorReintento = null;
      // El reintento agendado es justamente el que debe pasar el enfriamiento.
      return refresh({ ignorarEspera: true }).catch(() => {});
    }, esperaDeReintento());
  }

  function manejarFalloDeRefresh(e) {
    if (e.code === 'REFRESH_INVALID') {
      limpiar();
      emitir('guest');
    } else if (CODIGOS_DE_SESION.includes(e.code)) {
      limpiar();
      emitir('revoked', e.code, e.serverMessage);
    } else {
      // El server no está: conservamos el token y reintentamos en frío.
      ultimoFalloTransitorio = d.ahora();
      ultimoErrorTransitorio = e;
      emitir('unavailable', e.code);
      programarReintento();
    }
  }

  // conCandado(...) se invoca de forma síncrona: sin locks, la petición a
  // /refresh sale antes de que refresh() devuelva (single-flight observable en
  // el mismo tick). Con { forzar: true } se salta la guarda de frescura — lo
  // usan las capas HTTP ante un 401 TOKEN_EXPIRED, para que un reloj adelantado
  // no deje al cliente en bucle con un token que el server ya rechaza. Si ya
  // hay un refresco en vuelo se comparte, forzado o no.
  function refresh({ forzar = false, ignorarEspera = false } = {}) {
    if (refrescando === null) {
      if (
        !ignorarEspera &&
        ultimoErrorTransitorio &&
        d.ahora() - ultimoFalloTransitorio < ESPERA_REINTENTO_MS
      ) {
        return Promise.reject(ultimoErrorTransitorio);
      }
      refrescando = Promise.resolve(
        conCandado((trasCandado) => {
          return refrescarDeVerdad(trasCandado, forzar);
        })
      )
        .catch((e) => {
          if (e.code !== CODIGO_CANCELADO) manejarFalloDeRefresh(e);
          throw e;
        })
        .finally(() => {
          refrescando = null;
        });
    }
    return refrescando;
  }

  function alCambiarVisibilidad() {
    if (doc.visibilityState !== 'visible' || !accessToken) return;
    if (expiresAt - d.ahora() < UMBRAL_VISIBLE_MS) refresh().catch(() => {});
  }

  if (doc && typeof doc.addEventListener === 'function') {
    doc.addEventListener('visibilitychange', alCambiarVisibilidad);
  }

  async function boot() {
    try {
      d.storage().removeItem(CLAVE_VIEJA);
    } catch (e) {
      /* storage bloqueado: no importa */
    }
    try {
      await refresh();
      return 'session';
    } catch (e) {
      if (e.code === 'REFRESH_INVALID' || CODIGOS_DE_SESION.includes(e.code))
        return 'guest';
      return 'unavailable';
    }
  }

  async function login(email, password, subdomain) {
    const data = await pedir('/login', {
      body: { email, password, subdomain, platform: PLATAFORMA },
    });
    if (data.mfaRequired) return { mfaRequired: true, mfaToken: data.mfaToken };
    if (data.enrollmentRequired)
      return {
        enrollmentRequired: true,
        enrollmentToken: data.enrollmentToken,
      };
    // Un 200 sin token utilizable no es una sesión: dejarlo pasar mandaría al
    // usuario a la app sin token (mismo trato que en refrescarDeVerdad).
    if (!adoptar(data))
      throw new AuthError({
        code: 'NETWORK',
        message: 'respuesta sin token',
        status: 200,
      });
    return { session: data.session };
  }

  async function verifyTotp(mfaToken, totpCode) {
    const data = await pedir('/mfa/totp', { body: { mfaToken, totpCode } });
    if (!adoptar(data))
      throw new AuthError({
        code: 'NETWORK',
        message: 'respuesta sin token',
        status: 200,
      });
    return { session: data.session };
  }

  function enroll({ enrollmentToken } = {}) {
    const body = enrollmentToken ? { enrollmentToken } : {};
    return pedir('/mfa/totp/enroll', { body, conToken: !enrollmentToken });
  }

  async function activate({ enrollmentToken } = {}, totpCode) {
    const body = enrollmentToken ? { enrollmentToken, totpCode } : { totpCode };
    const data = await pedir('/mfa/totp/activate', {
      body,
      conToken: !enrollmentToken,
    });
    if (data.accessToken) {
      // Mismo trato que login/verifyTotp: un token que no se puede adoptar (sin
      // vencimiento utilizable) no es una sesión.
      if (!adoptar(data))
        throw new AuthError({
          code: 'NETWORK',
          message: 'respuesta sin token',
          status: 200,
        });
      return { session: data.session };
    }
    return { ok: true };
  }

  function session_({ silencioso = false } = {}) {
    return pedir('/session', { method: 'GET', conToken: true, silencioso });
  }

  async function logout() {
    try {
      await pedir('/logout', { conToken: true, sinReintento: true });
    } catch (e) {
      /* cerrar sesión con el server caído sigue siendo cerrar sesión */
    }
    limpiar();
    difundir({ type: 'logout' });
  }

  function adminResetMfa({ email, subdomain, reason }) {
    return pedir('/admin/mfa/reset', {
      body: { email, subdomain, reason },
      conToken: true,
    });
  }

  function adminUnlock({ email, subdomain, reason }) {
    return pedir('/admin/unlock', {
      body: { email, subdomain, reason },
      conToken: true,
    });
  }

  // Lo llaman las capas HTTP con el code (y el message) de cualquier respuesta
  // 401/403/503. El mensaje viaja porque BLACKLISTED se muestra con el texto
  // que manda el server (spec fase 3 §3.4), no con uno de catálogo.
  function recibirCodigo(code, message) {
    if (code === 'SESSION_UNAVAILABLE') {
      emitir('unavailable', code);
    } else if (CODIGOS_DE_SESION.includes(code)) {
      limpiar();
      emitir('revoked', code, message);
      difundir({ type: 'revoked', code, message });
    }
  }

  function estado() {
    return { accessToken, expiresAt, session, user: usuario };
  }

  // Suelta temporizadores, canal y listener de visibilidad (tests y HMR).
  function destruir() {
    cancelarTemporizador();
    cancelarReintento();
    if (canal) canal.close();
    if (doc && typeof doc.removeEventListener === 'function')
      doc.removeEventListener('visibilitychange', alCambiarVisibilidad);
  }

  return {
    getAccessToken: () => {
      return accessToken;
    },
    estado,
    on,
    boot,
    refresh,
    login,
    verifyTotp,
    enroll,
    activate,
    session: session_,
    logout,
    adminResetMfa,
    adminUnlock,
    recibirCodigo,
    destruir,
    // internos que Task 2 reutiliza
    _adoptar: adoptar,
    _limpiar: limpiar,
    _emitir: emitir,
    _pedir: pedir,
    _manejarFalloDeRefresh: manejarFalloDeRefresh,
    _deps: d,
  };
}

const authClient = crearAuthClient();
export default authClient;
